import type { ReactNode } from "react";
import { useCommitState } from "../data/usePage.js";

/**
 * The primary action(s) at the top of the right sidebar, contextual to the page
 * kind and edit state. Up to two buttons stack here, primary on top.
 *
 * Markdown (autosaves):
 *  - Publish — while editing (disabled until there's something to publish), or
 *    when the worktree has changes.
 *  - Edit — when not in the source overlay; primary when it's the only button,
 *    secondary when Publish is above it.
 *
 * Query (no autosave):
 *  - Publish — only when the worktree has changes (never shown disabled).
 *  - Save — always; blue (primary) when there are unsaved edits, outlined-white
 *    (secondary) and disabled when there's nothing to save.
 */
export function EditTopButton({
  kind,
  editing,
  dirty,
  busy,
  onEdit,
  onSave,
  onPublish,
  block = false,
  note = null,
}: {
  kind: "md" | "sql";
  editing: boolean;
  dirty: boolean;
  busy: "" | "save" | "publish";
  onEdit: () => void;
  onSave: () => void;
  onPublish: () => void;
  /** When true, disable the save/publish actions (e.g. data source is down). */
  block?: boolean;
  /** Small message rendered under the buttons (warning / error). */
  note?: ReactNode;
}) {
  const c = useCommitState();
  const ready = c.status === "ready";
  const ahead = ready ? c.data.ahead : 0;
  const changed = ready ? c.data.changed.length : 0;
  const worktreeChanges = changed > 0 || ahead > 0;
  // Publish saves first, so there's something to publish whenever the worktree
  // has changes OR there are unsaved edits.
  const canPublish = worktreeChanges || dirty;

  type Variant = "primary" | "secondary";
  // `icon` is the same action on a narrow window, where this card floats at the
  // top of the content instead of heading a sidebar (styles.css, RESPONSIVE).
  // Both render; CSS picks one, so there is no width to branch on in JS.
  type Btn = {
    key: string;
    label: string;
    icon: string;
    onClick: () => void;
    disabled: boolean;
    variant: Variant;
  };
  const btns: Btn[] = [];

  if (kind === "md") {
    // Markdown shows Publish while editing (disabled until there's something to
    // publish) or whenever the worktree has changes.
    if (editing || worktreeChanges) {
      btns.push({
        key: "publish",
        label: busy === "publish" ? "Publishing…" : "Publish",
        icon: "⇧",
        onClick: onPublish,
        disabled: busy !== "" || !canPublish,
        variant: "primary",
      });
    }
    if (!editing) {
      btns.push({
        key: "edit",
        label: "Edit",
        icon: "✎",
        onClick: onEdit,
        disabled: false,
        variant: btns.length === 0 ? "primary" : "secondary",
      });
    }
  } else {
    // Query: Publish only when there's genuinely something to publish (never a
    // disabled Publish). Save reflects unsaved edits via its color.
    if (worktreeChanges) {
      btns.push({
        key: "publish",
        label: busy === "publish" ? "Publishing…" : "Publish",
        icon: "⇧",
        onClick: onPublish,
        disabled: busy !== "",
        variant: "primary",
      });
    }
    btns.push({
      key: "save",
      label: busy === "save" ? "Saving…" : "Save",
      icon: "⤓",
      onClick: onSave,
      disabled: busy !== "" || !dirty,
      variant: dirty ? "primary" : "secondary",
    });
  }

  if (btns.length === 0 && !note) return null;

  return (
    <section className="dw-card dw-edit-actions">
      {btns.map((b) => (
        <button
          key={b.key}
          // When blocked (e.g. source down) keep the disabled button neutral —
          // a disabled blue button reads as "primary action available".
          className={block ? "secondary" : b.variant}
          disabled={b.disabled || block}
          onClick={b.onClick}
          // The icon carries no text, so the title is the label either way.
          title={b.label}
        >
          <span className="dw-btn-label">{b.label}</span>
          <span className="dw-btn-icon" aria-hidden="true">
            {b.icon}
          </span>
        </button>
      ))}
      {note}
    </section>
  );
}
